<template>
  <div class="no-data" v-if="hcpListData.hcpListDataItem && !hcpListData.hcpListDataItem.length">No data to show</div>
  <div v-scroll="cancelScrollToLastPosition" v-else>
    <div v-if="canExportHCPList && getFlag('DAS-772')">
      <v-layout class="mb-4">
        <div class="width-100 hvh-flex space-between align-center">
          <div style="display: flex; align-items: center">
            <h2 class="hvh-h5 pr-4">Top {{ Math.min(numOfHCPs || 50, 50) }} physicians</h2>
            <v-text-field v-model="hcpSearchText" append-icon="mdi-magnify" label="Search for a physician" @keydown.enter="searchForHcps" outline hide-details />
          </div>
          <div class="no-wrap">
            <v-btn-toggle class="hvh-btn hvh-btn--secondary my-0 mx-0" :value="filterObject.period" @change="emitChange('period', $event)" mandatory>
              <v-btn flat value="Yearly">Yearly</v-btn>
              <v-btn flat value="Monthly">Monthly</v-btn>
            </v-btn-toggle>
            <DownloadExportButton
              class="ml-1"
              :class="{ disabled: hcpListExportDisabled || isFiltering }"
              :numOfAnalytics="historyExportsData.count"
              :runId="historyExportsData.runId"
              :isLoading="isLoadingHistoryExportsData"
              @click="exportInfoDialogVisible = true"
              v-if="!exportLinearProgress"
            />
            <template v-else>
              <div class="hvh-relative">
                Export in progress...
                <v-progress-linear class="width-200px" :indeterminate="true" rounded height="6"></v-progress-linear>
                <button class="hvh-upper-right" @click="cancelRequest()">
                  <v-icon>mdi-close</v-icon>
                </button>
              </div>
            </template>
          </div>
        </div>
      </v-layout>
      <v-layout>
        <v-flex xs12>
          <HvhFreezeDataTable :class="{ disabled: isFiltering }" :headers="physicianHeaders" :items="tableData" :loading="isFiltering" />
          <Pagination :value="tableState.page" :total="numOfHCPs" :perPage="tableState.perPage" @input="pageChanged" v-if="hasPagination" />
        </v-flex>
      </v-layout>

      <!-- Pop-up Dialog for export details -->
      <HvhModal v-model="exportInfoDialogVisible" title="Export details" max-width="700">
        <template v-slot>
          <label>Please enter the description for the file to be exported: </label>
          <v-text-field clearable v-model="description"></v-text-field>
        </template>
        <template v-slot:actions>
          <v-btn flat class="hvh-btn hvh-btn--secondary" @click="exportInfoDialogVisible = false">Cancel</v-btn>
          <v-btn flat class="hvh-btn hvh-btn--primary" @click="exportToCSVNew()">Export</v-btn>
        </template>
      </HvhModal>
    </div>
    <div v-else>
      <v-layout class="mb-4">
        <div class="width-100 hvh-flex space-between align-center">
          <div style="display: flex; align-items: center">
            <h2 class="hvh-h5 pr-4">Top {{ Math.min(numOfHCPs || 50, 50) }} physicians</h2>
            <v-text-field v-model="hcpSearchText" append-icon="mdi-magnify" label="Search for a physician" @keydown.enter="searchForHcps" outline hide-details />
          </div>
          <div class="hvh-flex align-center">
            <v-btn-toggle class="hvh-btn hvh-btn--secondary my-0 mx-0" :value="filterObject.period" @change="emitChange('period', $event)" mandatory>
              <v-btn flat value="Yearly"> Yearly </v-btn>
              <v-btn flat value="Monthly"> Monthly </v-btn>
            </v-btn-toggle>
            <template v-if="canExportHCPList">
              <v-btn flat class="hvh-btn hvh-btn--secondary my-0 mx-0" :class="{ disabled: hcpListExportDisabled || isFiltering }" @click="exportToCSV" v-if="canExportHCPList && exportProgress == null">Export results</v-btn>
              <template v-else>
                <div class="hvh-relative ml-1">
                  Downloading...
                  <v-progress-linear class="width-200px" :value="exportProgress * 100" :buffer-value="100" :title="formatter.percentages(exportProgress)"></v-progress-linear>
                  <button class="hvh-upper-right" @click="cancelRequest()">
                    <v-icon>mdi-close</v-icon>
                  </button>
                </div>
              </template>
            </template>
            <v-btn flat class="hvh-btn hvh-btn--secondary my-0 mx-0" @click="orderList" v-else>Contact to Order List</v-btn>
          </div>
        </div>
      </v-layout>
      <v-layout>
        <v-flex xs12>
          <HvhFreezeDataTable :class="{ disabled: isFiltering }" :headers="physicianHeaders" :items="tableData" :loading="isFiltering" />
          <Pagination :value="tableState.page" :total="dataCount" :perPage="tableState.perPage" @input="pageChanged" v-if="hasPagination" ref="pagination" />
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import axios, { baseApiUrl } from '../../../../axios';
import { Debouncer, errorDebug, hashCode, isObject, isString, sanitize, valueDisplayFormat } from '../../../../libraries/common';
import { getRibonItems, stringifyLiveFilters, topEvents } from '../../../../libraries/liveFilters';
import HvhFreezeDataTable from '../../../common/freezeDataTable/HvhFreezeDataTable.vue';
import DownloadExportButton from './DownloadExportButton.vue';
import HvhModal from '../../../common/modal/HvhModal.vue';
import Pagination from '../../../common/dataTable/Pagination.vue';

export default {
  components: { HvhFreezeDataTable, DownloadExportButton, HvhModal, Pagination, },
  props: ['value', 'filterObject', 'numOfHCPs', 'hcpListExportDisabled', 'segment', 'runId', 'cohortName', 'analyticName', 'cohortId', 'decileSection',],
  data() {
    var labels = {
      ...(this.getFlag('DAS-799')) && { profileLink: { text: "Profile Link", route: "hcpProfile", params: { runId: this.runId, cohortId: 1 } } },
      npi: "NPI",
      lastName: "Last Name",
      firstName: "First Name",
      address: "Address",
      city: "City",
      state: "State",
      zip: "Zip Code",
      speciality: "Specialty Description",
      patientCount: "Num Patients",
      decile: "Decile",
    };

    return {
      exportLinearProgress: false,
      exportProgress: null,
      hcpSearchText: null,
      lastHcpSearchText: null,
      cancelRequest: null,
      formatter: valueDisplayFormat,
      showModal: false,
      labels,
      description: this.cohortName + " - HCP List",
      exportInfoDialogVisible: false,
      historyExportsData: {},
      isLoadingHistoryExportsData: false,
      tableState: { page: 1, perPage: 50 },
      pageSize: 1000,
      isFiltering: false,
      hcpListData: {},
      debouncer: new Debouncer(2000),
      scrollToLastPosition: () => null,
    }
  },
  computed: {
    physicianHeaders() {
      var headers = Object.keys(this.filterHeaders).length ? this.filterHeaders : this.labels;
      var self = this;
      // Hide decile column from HCP list if decile table is hidden
      if (!this.decileSection) {
        delete headers.decile;
      }

      var retVal = Object.keys(headers)
        .map(key => ({
          // valueFnc: (item) => {try { return item[`${key}`]; } catch(e) { console.error(`Property - ${key}.`, e); }},
          valueFnc: new Function('item', `try { return item.${key}; } catch(e) { console.error('Property - ${key}.', e); }`),
          text: typeof (headers[key]) == 'string' ? headers[key] : headers[key].text,
          routeObj: typeof (headers[key]) == 'object' ? headers[key] : { text: "", route: "" },
          routeParamsFnc: item => { try { return { runId: self.runId, cohortId: 1, npi: item.npi }; } catch (e) { console.error('Property - ${key}.', e); } },
          // routeParamsFnc: new Function('item', `try { return {runId:${this.runId}, cohortId:1, npi:item.npi}; } catch(e) { console.error('Property - ${key}.', e); }`),
          link: typeof (headers[key]) == 'object' ? true : false,
          sortable: false
        }));

      var link = retVal.filter(f => f.text == 'Profile Link')
      var nonLink = retVal.filter(f => f.text != 'Profile Link')

      retVal = [...link, ...nonLink]

      return retVal;
    },
    filterHeaders() {
      var hcpHeaders = this.hcpListData.hcpListHeaderItem || {};
      var ret = Object.keys(hcpHeaders)
        .filter(key => hcpHeaders[key])
        .reduce((acc, key) => {
          if (isObject(hcpHeaders[key])) {
            Object.assign(acc, hcpHeaders[key]);
          } else {
            acc[key] = hcpHeaders[key];
          }
          return acc;
        }, {});

      if (Object.keys(ret).length > 0 && this.getFlag('DAS-799')) {
        ret.profileLink = { text: "Profile Link", route: "hcpProfile" }
      }

      return ret;
    },
    physicianHeadersForExport() {
      var headers = Object.keys(this.filterHeadersForExport).length ? this.filterHeadersForExport : this.labels;
      return Object.keys(headers)
        .map(key => ({
          valueFnc: new Function('item', `try { return item.${key}; } catch(e) { console.error('Property - ${key}.', e); }`),
          text: headers[key],
          sortable: false
        }));
    },
    filterHeadersForExport() {
      var labels = {
        primaryHospitalId: "Primary Hospital Id",
        primaryLocationAddress: "Primary Location Address",
        primaryLocationCity: "Primary Location City",
        primaryLocationState: "Primary Location State",
        primaryLocationZipCode: "Primary Location ZipCode",

        physicianGroupId: "Physician Group Id",
        physicianGroupAddress: "Physician Group Address",
        physicianGroupCity: "Physician Group City",
        physicianGroupState: "Physician Group State",
        physicianGroupZip: "Physician Group Zipcode",

        idnId: "IDN Id",
        idnName: "IDN Name",
        idnAddress: "IDN Address",
        idnCity: "IDN City",
        idnState: "IDN State",
        idnZip: "IDN Zipcode"
      };

      var hcpHeaders = { ...this.hcpListData.hcpListHeaderItem, ...labels }
      return Object.keys(hcpHeaders)
        .filter(key => hcpHeaders[key])
        .reduce((acc, key) => {
          if (isObject(hcpHeaders[key])) {
            Object.assign(acc, hcpHeaders[key]);
          } else {
            acc[key] = hcpHeaders[key];
          }
          return acc;
        }, {});
    },
    topEvents() {
      return topEvents;
    },
    stringifiedFilterState() {
      return JSON.stringify({
        liveFilters: this.filterObject,
        description: this.description,
        parent_run_id: this.runId
      });
    },
    canExportHCPList() {
      return this.getFlag('DAS-903');
    },
    hasPagination() {
      return this.getFlag('DAS-1534') && this.numOfHCPs > this.tableState.perPage;
    },
    tableData() {
      var startIndex = (this.tableState.page - 1) * this.tableState.perPage;
      return (this.hcpListData.hcpListDataItem || [])
        .takeFromTo(startIndex, startIndex + this.tableState.perPage - 1);
    },
    filterHash() {
      return hashCode({ ...(this.filterObject || {}), decileFilter: null });
    },
    dataCount() {
      return this.lastHcpSearchText ? (this.hcpListData.hcpListDataItem || []).length : this.numOfHCPs;
    },
  },
  methods: {
    pageChanged(page) {
      this.scrollToLastPosition = () => {
        if (this.$refs.pagination)
          this.$nextTick(this.$refs.pagination.scrollToLastPosition);
      }

      var changeState = () => {
        this.tableState = {
          ...this.tableState,
          page,
        }

        this.scrollToLastPosition();
      }

      var startIndex = (page - 1) * this.tableState.perPage;
      if (startIndex + 1 >= (this.hcpListData.hcpListDataItem || []).length) {
        this.loadHcpListData(true)
          .then(changeState);
      } else {
        changeState();
      }
    },
    loadHcpListData(loadNext) {
      if (this.cancelRequest)
        this.cancelRequest();

      this.isFiltering = true;
      var hcpSearchText = this.hcpSearchText;
      return axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
        runId: this.runId,
        component: 'HCPList',
        startIndex: loadNext ? (this.hcpListData.hcpListDataItem || []).length : 0,
        pageSize: this.pageSize,
        ...stringifyLiveFilters(this.filterObject),
        searchText: hcpSearchText,
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelRequest = c)
      }).then(({ data }) => {
        this.hcpListData = {
          ...data,
          ...(loadNext ? { hcpListDataItem: (this.hcpListData.hcpListDataItem || []).concat(data.hcpListDataItem) } : {}),
        };
        this.isFiltering = false;
        this.lastHcpSearchText = hcpSearchText;

        if (!loadNext) {
          this.tableState = {
            ...this.tableState,
            page: 1,
          }
        }
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.hcpListData = {};
          this.isFiltering = false;
          this.disableIfTimeout(error);
        }
      });
    },
    searchForHcps() {
      var shouldSearchForHcps = !(this.hcpSearchText === this.lastHcpSearchText || (!this.hcpSearchText && !this.lastHcpSearchText));

      if (shouldSearchForHcps) {
        this.loadHcpListData();
      }
    },
    async getNewFileHandle(fileName) {
      const options = {
        suggestedName: fileName,
        types: [
          {
            description: 'CSV file',
            accept: {
              'text/plain': ['.csv'],
            },
          },
        ],
      };
      const handle = await window.showSaveFilePicker(options);
      return handle;
    },
    async exportToCSVStream() {
      var filterItems = getRibonItems(this.filterObject, this.segment)
        .map(f => `${f.title}: ${f.item}`);

      var result = new Uint8Array([]);
      var filterText = filterItems.length ? `"Applied filters:  ${filterItems.join(', ')}"\n` : null;
      if (filterText) {
        var encoder = new TextEncoder();
        result = encoder.encode(filterText);
      }

      const decoder = new TextDecoder();
      var numOfHcps = this.numOfHCPs;
      var bytesPerRow = null;

      var fileName = sanitize(`${this.cohortName}-${this.analyticName}-${this.runId}.csv`);

      this.exportProgress = 0;

      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json, text/plain, */*");
      myHeaders.append("Connection", "keep-alive");
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        runId: this.runId,
        component: 'HCPList',
        ...stringifyLiveFilters(this.filterObject),
        startIndex: 0,
        pageSize: 1000000000,
      });

      const controller = new AbortController()

      const requestOptions = {
        method: "POST",
        body: raw,
        headers: myHeaders,
        credentials: 'include',
        signal: controller.signal,
      };

      this.cancelRequest = () => controller.abort();

      fetch(`${baseApiUrl}Analytics/CohortAnalyticRun/StreamHCPList`, requestOptions)
        .then(async (response) => {
          const reader = response.body.getReader();
          let receivedLength = 0;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            var mergedArray = new Uint8Array(result.length + value.length);
            mergedArray.set(result);
            mergedArray.set(value, result.length);
            result = mergedArray;

            if (!bytesPerRow) {
              var decoded = decoder.decode(value).split('\n');
              if (decoded.length > 1) {
                bytesPerRow = value.length / decoded.length
              }
            }

            if (bytesPerRow)
              receivedLength += value.length / bytesPerRow;

            this.exportProgress = receivedLength / numOfHcps;
          }

          const blob = new Blob([result], { type: 'application/octet-stream' });
          saveAs(blob, fileName);
        })
        .catch(err => console.error(err))
        .then(() => this.exportProgress = null);
    },
    exportToCSV() {
      if (this.getFlag('DAS-1556'))
        return this.exportToCSVStream();

      var filterItems = getRibonItems(this.filterObject, this.segment)
        .map(f => `${f.title}: ${f.item}`);

      var step = 10000;
      var result = [filterItems.length ? `"Applied filters:  ${filterItems.join(', ')}"` : null,
      this.physicianHeadersForExport.map(f => f.text).join(',')].filter(f => f);
      var numOfHCPs = this.numOfHCPs;

      var downloadPage = (i) => {
        this.exportProgress = i * step / numOfHCPs;
        axios.post('/Analytics/CohortAnalyticRun/LiveResult', {
          runId: this.runId,
          component: 'HCPList',
          ...stringifyLiveFilters(this.filterObject),
          startIndex: i * step,
          pageSize: step,
        }, {
          cancelToken: new axios.CancelToken(c => this.cancelRequest = c)
        }).then(({ data }) => {
          var pushFnc = () => data.hcpListDataItem.forEach(f => result.push(this.physicianHeadersForExport.map(g => {
            var val = g.valueFnc(f);
            return isString(val) && val.indexOf(',') > -1 ? `"${val}"` : val;
          }).join(',')));
          if (data.hcpListDataItem.length < step) {
            pushFnc();
            var blob = new Blob([result.join('\n')], { type: "text/plain;charset=utf-8" });
            saveAs(blob, sanitize(`${this.cohortName}-${this.analyticName}-${this.runId}.csv`));
            this.exportProgress = null;
          } else {
            setTimeout(pushFnc, 10);
            downloadPage(++i);
          }
        }).catch((error) => {
          if (!axios.isCancel(error)) {
            console.log(error);
          }
          this.exportProgress = null;
        })
      };
      downloadPage(0);
    },
    exportToCSVNew() {
      this.exportInfoDialogVisible = false;
      this.exportLinearProgress = true;
      this.$notify('Generation of your file is in progress and you will receive an email when it is complete.')
      var postData = new FormData();
      postData.append("analyticName", "exporttask");
      postData.append('state', this.stringifiedFilterState);

      this.isRunning = true;
      axios.post('/Analytics/Cohort/' + this.cohortId + '/Run', postData, {
        cancelToken: new axios.CancelToken(c => this.cancelRequest = c)
      })
        .then(n => {
          if (n.data.isSuccess) {
            var runId = n.data.data;
            var checkStatus = () => {
              axios.post('/Analytics/Cohort/' + runId + '/ExportStatus')
                .then(response => {
                  var stat = Object.keys(response.data)[0];
                  this.exportLinearProgress = stat === "inprogress";
                  if (this.exportLinearProgress)
                    setTimeout(checkStatus, 5000);
                });
            }
            checkStatus();
          }
          else {
            this.exportLinearProgress = false;
            alert(n.data.message);
          }
        });
    },
    orderList() {
      this.$confirm('Contact to Order List', 'Your request will be submitted for further processing. One of our leads will be in touch shortly.\nThank you!')
        .then(() => axios.post(`/Analytics/CohortAnalyticRun/${this.runId}/Filters`, { name: this.saveFilterName, filter: JSON.stringify(this.filterObject) }))
        .then(({ data }) => axios.post(`/Analytics/CohortAnalyticRun/${this.runId}/OrderHCPList?filterSnapshotUUID=${data.uuid}`))
        .then(() => this.$notify('Request email is sent!'))
        .catch(() => null);
    },
    cancelScrollToLastPosition() {
      this.scrollToLastPosition = () => null;
    },
    emitChange(prop, value) {
      this.$emit('checked-changed', prop, value);
    },
  },
  watch: {
    filterHash() {
      this.debouncer.clear().push(() => {
        this.loadHcpListData();
      });
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisHcpList'] = this;
    this.loadHcpListData();

    this.isLoadingHistoryExportsData = true;
    axios.get(`/Analytics/CohortAnalyticRun/${this.runId}/ExportRunCount`)
      .then(({ data }) => this.historyExportsData = data)
      .catch(errorDebug)
      .then(() => this.isLoadingHistoryExportsData = false);
  }
}
</script>

<style lang="scss" scoped>
.no-data {
  padding-top: 15px;
  text-align: center;
  font-size: 14px;
}
</style>